/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE file for details)
*/

import React, { useState, useEffect } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import Repetition from "./Repetition";

const NestedForm = ({ questions, titles, index, data, setData, indexIntervention }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedSub, setSelectedSub] = useState(0);
  const intervention = data[index].interventions[indexIntervention];
  const methodOptions = selectedIndex < questions.length ? questions[selectedIndex] : [];
  const selectedMethodIndex =
    intervention.interventionMethod && methodOptions.includes(intervention.interventionMethod)
      ? methodOptions.indexOf(intervention.interventionMethod)
      : '';

  const selectedTypeIndex = titles.findIndex(
    (t) => t.label === intervention.interventionType
  );


      

  useEffect(() => {
    const intervention = data[index].interventions[indexIntervention];
    const indexTitle = titles.findIndex(t => t.label === intervention.interventionType);
    if (indexTitle !== -1) {
      setSelectedIndex(indexTitle);
    }
    if (indexTitle < questions.length) {
      const methodIndex = 
      indexTitle >= 0 && indexTitle < questions.length
        ? questions[indexTitle].indexOf(intervention.interventionMethod)
        : -1;
          if (methodIndex !== -1) {
        setSelectedSub(methodIndex);
      }
    }
  }, [data, index, indexIntervention, questions, titles]);

  // mise à jour du tableau data
  const handleChange = (e) => {
    const newData = [...data];
    const newInterventions = [...newData[index].interventions];
    newInterventions[indexIntervention] = {
      ...newInterventions[indexIntervention],
      [e.target.name]: e.target.value,
    };
    newData[index] = { ...newData[index], interventions: newInterventions };
    setData(newData);
  };

  const handleTypeChange = (e) => {
    const idx = Number(e.target.value);
    setSelectedIndex(idx);
    setSelectedSub(0);
    const newData = [...data];
    const newInterventions = [...newData[index].interventions];
    newInterventions[indexIntervention] = {
      ...newInterventions[indexIntervention],
      interventionType: titles[idx].label,
      interventionMethod: "",
    };
    newData[index] = { ...newData[index], interventions: newInterventions };
    setData(newData);
  };

  const handleMethodChange = (e) => {
    const idx = Number(e.target.value);
    setSelectedSub(idx);
    const newData = [...data];
    const newInterventions = [...newData[index].interventions];
    newInterventions[indexIntervention] = {
      ...newInterventions[indexIntervention],
      interventionMethod: questions[selectedIndex][idx],
    };
    newData[index] = { ...newData[index], interventions: newInterventions };
    setData(newData);
  };

  return (
    <div>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="interventionTypeLabel">Select an intervention type</InputLabel>
        <Select
          id="interventionType"
          name="interventionType"
          label="Select an intervention type"
          value={selectedTypeIndex !== -1 ? selectedTypeIndex : ''}
          onChange={handleTypeChange}
        >
          {titles.map((title, idx) => (
          <MenuItem key={idx} value={idx}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {title.icon && (
              <img
                src={title.icon}
                alt={title.label}
                style={{ width: 24, height: 24 }}
              />
            )}
            <span style={{ fontSize: "1.1rem", fontWeight: 500 }}>
              {title.label}
            </span>
          </div>
        </MenuItem>
))}

        </Select>
      </FormControl>

      {selectedIndex < questions.length && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="methodLabel">Select a method</InputLabel>
          <Select
            id="method"
            name="interventionMethod"
            label="Select a method"
            value={selectedMethodIndex}
            onChange={handleMethodChange}
          >
            {methodOptions.map((subQ, subIdx) => (
              <MenuItem key={subIdx} value={subIdx}>
                {subQ}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedIndex < questions.length &&
        selectedSub === methodOptions.length - 1 && (
          <TextField
            id="interventionMethodOther"
            name="interventionMethod"
            label="Specify method"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={intervention.interventionMethod || ""}
            onChange={handleChange}
          />
        )}

      {selectedIndex === questions.length && (
        <TextField
          id="interventionTypeOther"
          name="interventionType"
          label="Intervention type"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={intervention.interventionType || ""}
          onChange={handleChange}
        />
      )}

      <Repetition index={index} data={data} setData={setData} indexIntervention={indexIntervention} />
    </div>
  );
};

export default function TypeIntervention({ index, data, setData, indexIntervention }) {
  const subCategories = [
    [
      "By oral gavage",
      "By tail intravenous injection",
      "By hydrodynamic tail vein injection",
      "By retro-orbital injection",
      "By intraperitoneal injection",
      "By subcutaneous injection",
      "By intramuscular injection",
      "In the drinking water",
      "In the food",
      "By means of a micropipette",
      "Other"
    ],
    [
      "Brain stereotaxic injection",
      "Brain viral injection (no stereotaxic)",
      "Placement of head implants",
      "In utero surgery",
      "Transient renal ischemia",
      "Telemetric implants for blood pressure",
      "Osmotic mini pumps implants",
      "Other surgery"
    ],
    [
      "Pole test",
      "Running Wheel Test",
      "Open Field",
      "CatWalk",
      "Rotarod",
      "Y-maze",
      "T-maze",
      "Novel object recognition",
      "Grid walk",
      "Other behavioral test"
    ],
    [
      "Glucose tolerance test",
      "Labmaster",
      "Metabolic cage",
      "Insulin tolerance test",
      "Pyruvate tolerance test",
      "Measuring muscle strength",
      "Body weight measurement",
      "Other physiological test"
    ],
    [
      "Electrical microstimulation",
      "Optogenetic stimulation",
      "Fiber photometry recording",
      "Intrinsic signal and wide-field fluorescence imaging",
      "In vivo neuroelectrophysiology",
      "Electromyography",
      "Electrocardiogram measurements",
      "Other electrophysiological test"
    ],
    [
      "Total body Irradiation",
      "X-ray imaging",
      "Magnetic Resonance Imaging",
      "EchoMRI",
      "Other irradiation (please specify)"
    ],
    [
      "Subcutaneous transplantation of tumor cells",
      "Mammary fat pad transplantation of tumor cells",
      "Liver cancer induction",
      "Ovarian cancer induction",
      "Orthotopic lung tumor implantation",
      "Other tumor induction method"
    ],
    [
      "Blood sampling",
      "Biopsy",
      "CSF collection",
      "Feces collection",
      "Urine collection",
      "Other collection of samples"
    ],
    [
      "Computed Tomography",
      "Bioluminescence imaging",
      "Magnetic Resonance Imaging",
      "Positron emission tomography",
      "Micro-tomography",
      "Other imaging procedure"
    ],
    [
      "Ear clipping",
      "Shaving",
      "Pen marking",
      "Phalangeal section",
      "Other marking procedure"
    ],
    [
      "Euthanasia",
      "Perfusion, fixation and euthanasia",
      "Blood collection and euthanasia",
      "Rehoming",
      "Use for breeding",
      "Re-use in another experiment",
      "Other terminal intervention"
    ]
  ];

  const titles = [
    { value: "administration", label: "Administration of a product/treatment", icon: "/icons/Treatment.svg" },
    { value: "surgery", label: "Surgery", icon: "/icons/Surgery.svg"},
    { value: "behavioral", label: "Behavioral test", icon: "/icons/BehaviorTest.svg" },
    { value: "physiological", label: "Physiological test", icon: "/icons/PhysiologicalTest.svg" },
    { value: "electrophysiological", label: "Electrophysiological test", icon: "/icons/ElectrophysiologicalTest.svg" },
    { value: "irradiation", label: "Irradiation", icon: "/icons/Irradiation.svg" },
    { value: "tumor", label: "Tumor induction", icon: "/icons/TumorInduction.svg" },
    { value: "collection", label: "Collection of samples", icon: "/icons/CollectionOfSamples.svg" },
    { value: "imaging", label: "Imaging", icon: "/icons/Imaging.svg" },
    { value: "marking", label: "Marking", icon: "/icons/Marking.svg" },
    { value: "terminal", label: "Terminal intervention", icon: "/icons/TerminalIntervention.svg" },
    { value: "other", label: "Other" },
];

  return (
    <div className="p-6">
      <NestedForm questions={subCategories} titles={titles} index = {index} data={data} setData={setData} indexIntervention={indexIntervention}/>
    </div>
  );
}
